import React, { useState, useEffect, useCallback } from 'react';

/**
 * AdminArchive — Searchable archive of settled pools with fee breakdown.
 *
 * Shows per-pool: time, coin, pool total, 3% fee, bet count, outcome.
 * Filters: coin, date range. Paginated.
 */
const AdminArchive = () => {
    const [pools, setPools] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    // Filters
    const [coin, setCoin] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchArchive = useCallback(async (pg = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pg, limit: 50 });
            if (coin) params.set('coin', coin);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);

            const res = await fetch(`/api/admin/archive?${params}`, { credentials: 'include' });
            const data = await res.json();
            setPools(data.pools || []);
            setTotal(data.total || 0);
            setPage(data.page || 1);
            setTotalPages(data.totalPages || 1);
        } catch { /* ignore */ }
        setLoading(false);
    }, [coin, dateFrom, dateTo]);

    useEffect(() => { fetchArchive(1); }, [fetchArchive]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchArchive(1);
    };

    const goPage = (pg) => {
        setPage(pg);
        fetchArchive(pg);
    };

    // Compute summary totals
    const totalPoolSum = pools.reduce((a, p) => a + parseFloat(p.pool_total || 0), 0);
    const totalFeeSum = pools.reduce((a, p) => a + parseFloat(p.total_fees || 0), 0);

    return (
        <div className="space-y-4">
            {/* Filters */}
            <form onSubmit={handleSearch} className="rounded-lg p-4 border border-white/10 bg-white/[0.02]">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-3">
                    Revenue Archive — Pool Fees
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <select
                        value={coin}
                        onChange={(e) => setCoin(e.target.value)}
                        className="bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-blue-500/50 appearance-none cursor-pointer"
                    >
                        <option value="">All Coins</option>
                        <option value="bitcoin">Bitcoin</option>
                        <option value="ethereum">Ethereum</option>
                        <option value="solana">Solana</option>
                    </select>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 uppercase font-bold shrink-0">From</span>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="flex-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-blue-500/50 font-mono"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 uppercase font-bold shrink-0">To</span>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="flex-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-blue-500/50 font-mono"
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-500 text-white rounded px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"
                    >
                        Search
                    </button>
                    {(coin || dateFrom || dateTo) && (
                        <button
                            type="button"
                            onClick={() => { setCoin(''); setDateFrom(''); setDateTo(''); }}
                            className="text-xs text-red-400 hover:text-red-300 underline flex items-center justify-center"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </form>

            {/* Summary */}
            <div className="flex gap-4">
                <SumBadge label="Pools" value={total.toLocaleString()} color="blue" />
                <SumBadge label="Total Pool Volume" value={`${totalPoolSum.toFixed(2)} SOL`} color="emerald" />
                <SumBadge label="Total Fees (3%)" value={`${totalFeeSum.toFixed(4)} SOL`} color="amber" />
            </div>

            {/* Table */}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/10 bg-white/[0.03]">
                                <th className="px-3 py-2.5 text-left text-sm uppercase tracking-wider text-gray-500 font-bold">Time</th>
                                <th className="px-3 py-2.5 text-left text-sm uppercase tracking-wider text-gray-500 font-bold">Coin</th>
                                <th className="px-3 py-2.5 text-center text-sm uppercase tracking-wider text-gray-500 font-bold">Outcome</th>
                                <th className="px-3 py-2.5 text-right text-sm uppercase tracking-wider text-gray-500 font-bold">UP Pool</th>
                                <th className="px-3 py-2.5 text-right text-sm uppercase tracking-wider text-gray-500 font-bold">DOWN Pool</th>
                                <th className="px-3 py-2.5 text-right text-sm uppercase tracking-wider text-gray-500 font-bold">Total Pool</th>
                                <th className="px-3 py-2.5 text-right text-sm uppercase tracking-wider text-amber-500/70 font-bold">Fee (3%)</th>
                                <th className="px-3 py-2.5 text-right text-sm uppercase tracking-wider text-gray-500 font-bold">Bets</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-600">Loading...</td></tr>
                            ) : pools.length === 0 ? (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-600 italic">No settled pools found</td></tr>
                            ) : (
                                pools.map((pool) => {
                                    const poolTotal = parseFloat(pool.pool_total || 0);
                                    const fees = parseFloat(pool.total_fees || 0);
                                    return (
                                        <tr key={pool.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                                            <td className="px-3 py-2 font-mono text-sm text-white">
                                                {new Date(pool.slot_time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-white uppercase font-bold">{pool.coin_market?.slice(0, 4)}</td>
                                            <td className="px-3 py-2 text-center">
                                                <span className={`text-sm px-2 py-0.5 rounded font-black uppercase ${
                                                    pool.outcome === 'UP'
                                                        ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                                                        : pool.outcome === 'DOWN'
                                                        ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                                                        : 'bg-white/10 text-gray-400'
                                                }`}>
                                                    {pool.outcome || '—'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-right text-green-400">
                                                {parseFloat(pool.total_up_sol || 0).toFixed(2)}
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-right text-red-400">
                                                {parseFloat(pool.total_down_sol || 0).toFixed(2)}
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-right text-white font-bold">
                                                {poolTotal.toFixed(2)}
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-right text-amber-400 font-bold">
                                                {fees.toFixed(4)}
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-right text-white">
                                                {pool.bet_count}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-2">
                    <span className="text-xs text-gray-500 font-mono">
                        Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => goPage(page - 1)}
                            disabled={page <= 1}
                            className="px-3 py-1.5 text-xs font-bold uppercase rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            ← Prev
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pg;
                            if (totalPages <= 5) pg = i + 1;
                            else if (page <= 3) pg = i + 1;
                            else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                            else pg = page - 2 + i;
                            return (
                                <button
                                    key={pg}
                                    onClick={() => goPage(pg)}
                                    className={`px-2.5 py-1.5 text-xs font-mono rounded border transition-colors ${
                                        pg === page
                                            ? 'bg-blue-600 border-blue-500 text-white'
                                            : 'border-white/10 text-gray-500 hover:text-white hover:border-white/30'
                                    }`}
                                >
                                    {pg}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => goPage(page + 1)}
                            disabled={page >= totalPages}
                            className="px-3 py-1.5 text-xs font-bold uppercase rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const SumBadge = ({ label, value, color }) => (
    <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded border border-${color}-500/20 bg-${color}-500/5`}>
        <span className={`text-sm uppercase tracking-wider text-${color}-500/70 font-bold`}>{label}</span>
        <span className={`text-sm font-mono font-bold text-${color}-400`}>{value}</span>
    </div>
);

export default AdminArchive;
